import React, { useState } from 'react';
import axios from 'axios';
import Reveal from './Reveal';
import Newsletter from './Newsletter';
import { Toaster, toast } from 'sonner';
import { FOOTER, NAV_LINKS } from '../lib/data';
import { useLanguage } from '../lib/LanguageContext';

const API = '/api';

const FOOTER_CONTENT = {
  en: {
    inquiryLabel: 'Inquiries — Press, curatorial & studio',
    heading: 'Begin a correspondence.',
    fields: {
      name: 'Name',
      email: 'Email',
      message: 'Message',
    },
    validation: {
      completeAll: 'Please complete all fields.',
      success: 'Thank you — your message has been received.',
      error: 'Something went wrong. Please try again.',
      emailRequired: 'Please enter your email.',
      newsletterSuccess: 'Thank you — you have been added to the newsletter.',
    },
    submit: 'Send inquiry',
    submitting: 'Sending…',
    tagline: FOOTER.tagline,
    newsletter: 'Newsletter',
    emailPlaceholder: 'Email address',
    join: 'Join',
    navigate: 'Navigate',
    follow: 'Follow',
    rights: 'All rights reserved',
    nav: NAV_LINKS.map((link) => link.label),
  },
  ro: {
    inquiryLabel: 'Solicitări — Presă, curatorial & studio',
    heading: 'Luați legătura cu noi.',
    fields: {
      name: 'Nume',
      email: 'Email',
      message: 'Mesaj',
    },
    validation: {
      completeAll: 'Te rugăm să completezi toate câmpurile.',
      success: 'Mulțumim — mesajul tău a fost primit.',
      error: 'A apărut o eroare. Te rugăm să încerci din nou.',
      emailRequired: 'Te rugăm să introduci adresa de email.',
      newsletterSuccess: 'Mulțumim — ai fost adăugat în newsletter.',
    },
    submit: 'Trimite solicitarea',
    submitting: 'Se trimite…',
    tagline: 'Platformă de artă contemporană și expoziții',
    newsletter: 'Newsletter',
    emailPlaceholder: 'Adresă de email',
    join: 'Abonare',
    navigate: 'Navigare',
    follow: 'Urmărește',
    rights: 'Toate drepturile rezervate',
    nav: ['Expoziții', 'Artiști', 'Despre', 'Contact', 'Evenimente private', 'Consultanță în artă', 'Jurnal'],
  },
};

const Footer = () => {
  const { language } = useLanguage();
  const content = FOOTER_CONTENT[language] || FOOTER_CONTENT.en;
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(content.validation.completeAll);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, form);
      if (!response.data?.id) throw new Error("Inquiry not confirmed");
      toast.success(content.validation.success);
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error(content.validation.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="contact"
      className="relative bg-ink text-bone pt-24 md:pt-32 lg:pt-40 pb-10"
      data-testid="site-footer"
    >
      <Toaster position="bottom-center" theme="dark" />
      <div className="px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-x-6 gap-y-14">
          <Reveal className="col-span-12 md:col-span-7">
            <span className="text-[11px] tracking-eyebrow uppercase text-bone/60">
              {content.inquiryLabel}
            </span>
            <h2
              className="mt-6 font-sans font-extrabold leading-[0.92] tracking-normal text-5xl sm:text-6xl md:text-7xl lg:text-[6vw]"
              data-testid="contact-heading"
            >
              {content.heading}
            </h2>
          </Reveal>

          <Reveal delay={0.1} className="col-span-12 md:col-span-5 md:col-start-8 md:mt-24">
            <form onSubmit={onSubmit} className="space-y-7" data-testid="contact-form">
              <Field
                label={content.fields.name}
                name="name"
                value={form.name}
                onChange={onChange}
                testid="contact-input-name"
              />
              <Field
                label={content.fields.email}
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                testid="contact-input-email"
              />
              <TextareaField
                label={content.fields.message}
                name="message"
                value={form.message}
                onChange={onChange}
                testid="contact-input-message"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="mt-4 inline-flex items-center gap-3 text-[11px] tracking-eyebrow uppercase text-bone border-t border-bone/40 pt-4 pr-2 hover:opacity-60 transition-opacity duration-500 disabled:opacity-40"
              >
                <span>{loading ? content.submitting : content.submit}</span>
                <span>→</span>
              </button>
            </form>
          </Reveal>
        </div>

        <div className="mt-24 md:mt-32 hairline-on-dark" />

        <div className="mt-10 grid grid-cols-12 gap-x-6 gap-y-10">
          <div className="col-span-12 md:col-span-4">
            <div
              className="font-sans font-extrabold text-3xl md:text-4xl leading-none tracking-[0.04em]"
            >
              {FOOTER.brand}
            </div>
            <p className="mt-3 text-bone/60 text-sm font-bold">{content.tagline}</p>
            <Newsletter />
          </div>

          <div className="col-span-6 md:col-span-2">
            <span className="text-[11px] tracking-eyebrow uppercase text-bone/50">{content.navigate}</span>
            <ul className="mt-4 space-y-2">
              {NAV_LINKS.map((l, index) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-bone/80 text-sm hover:opacity-60 transition-opacity duration-500"
                  >
                    {content.nav[index] || l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-6 md:col-span-3">
            <span className="text-[11px] tracking-eyebrow uppercase text-bone/50">Studio</span>
            <ul className="mt-4 space-y-2 text-bone/80 text-sm">
              {FOOTER.address.map((a) => <li key={a}>{a}</li>)}
              {FOOTER.contact.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>

          <div className="col-span-12 md:col-span-3">
            <span className="text-[11px] tracking-eyebrow uppercase text-bone/50">{content.follow}</span>
            <ul className="mt-4 space-y-2">
              {FOOTER.socials.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.label === 'Newsletter' ? '#newsletter' : s.href}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="text-bone/80 text-sm hover:opacity-60 transition-opacity duration-500"
                  >
                    {s.label} →
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[11px] tracking-eyebrow uppercase text-bone/40">
          <span>© {new Date().getFullYear()} DIMIRXSON · {content.rights}</span>
        </div>
      </div>
    </footer>
  );
};

const Field = ({ label, name, value, onChange, type = 'text', testid }) => (
  <label className="block">
    <span className="text-[11px] tracking-eyebrow uppercase text-bone/50">{label}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="mt-2 w-full bg-transparent border-b border-bone/30 focus:border-bone/80 outline-none py-2 text-bone placeholder-bone/30 text-base font-bold transition-colors duration-500"
    />
  </label>
);

const TextareaField = ({ label, name, value, onChange, testid }) => (
  <label className="block">
    <span className="text-[11px] tracking-eyebrow uppercase text-bone/50">{label}</span>
    <textarea
      name={name}
      rows={3}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="mt-2 w-full bg-transparent border-b border-bone/30 focus:border-bone/80 outline-none py-2 text-bone placeholder-bone/30 text-base font-bold resize-none transition-colors duration-500"
    />
  </label>
);

export default Footer;
